import { TFunction } from 'i18next';
import { getFirstDayOfWeek } from '@youshift/shared/utils';
import { SupportedLocale } from '@youshift/shared/types';

import { Day, Month } from './types';

export const generateMonths = (
  currentYear: number,
  startDate: Date,
  endDate: Date,
  t: TFunction,
  locale: SupportedLocale,
): Month[] => {
  const months: Month[] = [];
  const monthNames = [
    t('calendars.january'),
    t('calendars.february'),
    t('calendars.march'),
    t('calendars.april'),
    t('calendars.may'),
    t('calendars.june'),
    t('calendars.july'),
    t('calendars.august'),
    t('calendars.september'),
    t('calendars.october'),
    t('calendars.november'),
    t('calendars.december'),
  ];

  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth();

  // Only generate months for the current year
  if (currentYear < startYear || currentYear > endYear) {
    return [];
  }

  const monthStart = currentYear === startYear ? startMonth : 0;
  const monthEnd = currentYear === endYear ? endMonth : 11;

  for (let month = monthStart; month <= monthEnd; month++) {
    const firstDay = new Date(currentYear, month, 1);
    const lastDay = new Date(currentYear, month + 1, 0);
    const days: Day[] = [];

    // Get the first day of the week based on locale
    const firstDayOfWeek = getFirstDayOfWeek(locale);
    const firstDayOfMonth = firstDay.getDay();

    // Calculate how many days from previous month to show
    let daysFromPrevMonth = (firstDayOfMonth - firstDayOfWeek + 7) % 7;
    if (firstDayOfWeek === 7) {
      // Sunday first
      daysFromPrevMonth = firstDayOfMonth;
    }

    // Add days from previous month
    const prevMonthLastDay = new Date(currentYear, month, 0).getDate();
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const date = new Date(currentYear, month - 1, prevMonthLastDay - i);
      days.push({
        date: date.toISOString().split('T')[0],
        isCurrentMonth: false,
      });
    }

    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(currentYear, month, day);
      days.push({
        date: date.toISOString().split('T')[0],
        isCurrentMonth: true,
        isToday: date.toDateString() === new Date().toDateString(),
      });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(currentYear, month + 1, day);
      days.push({
        date: date.toISOString().split('T')[0],
        isCurrentMonth: false,
      });
    }

    months.push({
      name: monthNames[month],
      days,
    });
  }

  return months;
};
